import React, { useState, useMemo, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Button, Box, Typography, TextField, IconButton, Chip, TableSortLabel, Tooltip } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { GroupedTableVirtuoso } from 'react-virtuoso';
import { ktpIcon, opnIcon, pillarIcon, projectStatusOreolIcon, rlndIcon, runnIcon, zvzIcon } from '../../layers/icons';
import ComplectExpanded from './ComplectExpanded';

// Определение столбцов таблицы
const columns = [
    {
        id: 'checkbox', label: '', minWidth: 20, width: 30,
        align: 'center',
    },
    // {
    //     id: 'location', label: '', minWidth: 40, width: 40,
    //     align: 'center',
    // },
    {
        id: 'pillar', label: 'Имя', minWidth: 150, width: 180,
        align: 'center',
    },
    {
        id: 'complect', label: 'Комплектация', minWidth: 100, width: 100,
        align: 'center',
    },
    {
        id: 'retreat', label: 'Отступление', minWidth: 150, width: 220,
        align: 'center',
    },
    {
        id: 'transformer', label: 'ТП', minWidth: 150, width: 150,
        align: 'center',
    },
    {
        id: 'type', label: 'Тип', minWidth: 100, width: 120,
        align: 'center',
    },
    {
        id: 'category', label: 'Категория', minWidth: 100, width: 120,
        align: 'center',
    },
];






// Компоненты для виртуализации таблицы
const createVirtuosoTableComponents = (darkMode) => ({
    Scroller: React.forwardRef((props, ref) => (
        <TableContainer
            component={Paper}
            {...props}
            ref={ref}
            sx={{
                '& .MuiTable-root': {
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                },
                // Кастомные стили скролла
                '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: darkMode ? '#2a2a2a' : '#f1f1f1',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: darkMode ? '#555' : '#c1c1c1',
                    borderRadius: '4px',
                    '&:hover': {
                        background: darkMode ? '#777' : '#a8a8a8',
                    },
                },
                '&::-webkit-scrollbar-corner': {
                    background: darkMode ? '#2a2a2a' : '#f1f1f1',
                },
                // Для Firefox
                scrollbarWidth: 'thin',
                scrollbarColor: darkMode ? '#555 #2a2a2a' : '#c1c1c1 #f1f1f1',
            }}
        />
    )),
    Table: (props) => (
        <Table {...props} size="small" />
    ),
    TableHead: React.forwardRef((props, ref) => (
        <TableHead {...props} ref={ref} />
    )),
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
});

const ObjectsTable = React.forwardRef(({
    objects,
    setObjects,
    transformers,
    smallMode,
    mode,
    disabledObjects,
    setDisabledObjects,
    selectedItems,
    disabledItems,
    defaultSelectedItems,
    defaultDisabledItems,
    setSelectedItems,
    setDisabledItems,
    handleChangeType,
    specs,
    searchText,
    searchInComplect,
    selectObject,
    map,
    itemMetadata = {},
    darkMode = false
}, ref) => {

    // Состояние для измененных данных
    const [changedObjects, setChangedObjects] = useState({});

    // Состояние для отслеживания раскрытых строк комплектации
    const [expandedComplect, setExpandedComplect] = useState({});

    // Состояние для хранения ID найденных элементов
    const [foundItemsIds, setFoundItemsIds] = useState({});

    // Состояние для отслеживания изменений типов опор
    const [typeChangedObjects, setTypeChangedObjects] = useState({});

    // Внутренние состояния для сортировки
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

    // Ref для навигации по группам
    const virtuosoRef = React.useRef(null);

    // Функция сортировки
    const handleSort = useCallback((key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }, [sortConfig]);

    // Функция для проверки, содержит ли элемент поисковый запрос
    const matchesSearch = useCallback((item, searchTerms) => {
        if (!searchTerms) return true;

        return (
            searchTerms.some(term => item.name?.toLowerCase().includes(term)) ||
            searchTerms.some(term => item.type_marka_docs?.toLowerCase().includes(term)) ||
            searchTerms.some(term => item.factory_manufacturer?.toLowerCase().includes(term))
        );
    }, []);


    // Функция для проверки, содержит ли элемент или его дочерние элементы поисковый запрос
    const matchesSearchRecursive = useCallback((item, searchTerms) => {
        if (!searchTerms) return true;

        // Проверяем сам элемент
        if (matchesSearch(item, searchTerms)) {
            return true;
        }

        // Проверяем дочерние элементы рекурсивно
        if (item.complect) {
            return item.complect.some(child => matchesSearchRecursive(child, searchTerms));
        }

        return false;
    }, [matchesSearch]);

    // Функция поиска в комплектации
    const searchInComplectFunction = useCallback((object, searchTerms) => {
        if (!object.mounted?.complect_filtered || !searchTerms) return true;

        return object.mounted.complect_filtered.some(cf =>
            cf.complect.some(item => matchesSearchRecursive(item, searchTerms))
        );
    }, [matchesSearchRecursive]);

    // Функция для подсветки найденного текста
    const highlightText = useCallback((text, searchTerms) => {
        if (!searchTerms || !text) return text;

        const regex = new RegExp(`(${searchTerms.join('|')})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span key={index} style={{ backgroundColor: darkMode ? '#ffb74d' : '#ffeb3b', fontWeight: 'bold', color: darkMode ? '#000' : 'inherit' }}>
                    {part}
                </span>
            ) : part
        );
    }, [darkMode]);

    // Функция для автоматического раскрытия найденных элементов
    const expandFoundItems = useCallback((complect, searchTerms, parentId = '') => {
        if (!searchTerms) return { expandedItems: [], foundItems: [] };

        const expandedItems = [];
        const foundItems = [];

        complect.forEach((item, index) => {
            const itemId = parentId ? `${parentId}_${item.id}` : `${item.id}`;

            // Если элемент содержит поисковый запрос, добавляем его и всех родителей
            if (matchesSearch(item, searchTerms)) {
                expandedItems.push(itemId);
                foundItems.push(itemId);
            }

            // Рекурсивно проверяем дочерние элементы
            if (item.complect) {
                const childResult = expandFoundItems(item.complect, searchTerms, itemId);
                if (childResult.foundItems.length > 0) {
                    expandedItems.push(itemId); // Добавляем родительский элемент
                    expandedItems.push(...childResult.expandedItems);
                    foundItems.push(...childResult.foundItems);
                }
            }
        });

        return { expandedItems, foundItems };
    }, [matchesSearch]);

    // Функция получения отсортированных данных
    const getSortedData = useCallback(() => {
        const sortedObjects = {
            pillars: [...objects.pillars],
            zvz: [...objects.zvz],
            opn: [...objects.opn],
            rlnd: [...objects.rlnd],
            transformers: [...objects.transformers],
            runn: [...objects.runn]
        };

        const sortArray = (array) => {
            if (!sortConfig.key) return array;

            return array.sort((a, b) => {
                let aValue, bValue;
                let type = 'string'
                switch (sortConfig.key) {
                    case 'number':
                        aValue = parseInt(a.name.split('.')[0]);
                        bValue = parseInt(b.name.split('.')[0]);
                        break;
                    case 'pillar':
                    case 'name':
                        aValue = a.name;
                        bValue = b.name;
                        break;
                    case 'transformer':
                        aValue = a.ktp?.name;
                        bValue = b.ktp?.name;
                        break;
                    case 'category':
                        aValue = a.mounted?.category || '';
                        bValue = b.mounted?.category || '';
                        break;
                    case 'type':
                        aValue = a.mounted?.name || '';
                        bValue = b.mounted?.name || '';
                        break;
                    default:
                        aValue = a[sortConfig.key];
                        bValue = b[sortConfig.key];
                }
                if (!aValue || !bValue) {
                    aValue = a.fid
                    bValue = b.fid
                    type = 'number'
                }
                if (type === 'string') {
                    return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                }
                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        };

        function search(array, searchText) {
            if (!searchText) return array;
            const searchTerms = searchText.toLowerCase().trim().split(' ').filter(word => word.length > 0);
            return array.filter(item => {
                // Основной поиск по имени
                const nameMatch = searchTerms.some(term => item.name.toLowerCase().includes(term));

                // Если включен поиск в комплектации, ищем также в комплектации
                if (searchInComplect && searchInComplectFunction(item, searchTerms)) {
                    return true;
                }

                return nameMatch;
            });
        }

        return {
            pillars: sortArray(search(sortedObjects.pillars, searchText)),
            zvz: sortArray(search(sortedObjects.zvz, searchText)),
            opn: sortArray(search(sortedObjects.opn, searchText)),
            rlnd: sortArray(search(sortedObjects.rlnd, searchText)),
            transformers: sortArray(search(sortedObjects.transformers, searchText)),
            runn: sortArray(search(sortedObjects.runn, searchText))
        };
    }, [sortConfig, searchText, objects, searchInComplect, searchInComplectFunction]);

    // Синхронизируем изменения с родительским компонентом
    React.useEffect(() => {
        if (Object.keys(changedObjects).length > 0) {
            // Здесь можно добавить логику для отправки изменений на сервер
            // или уведомления родительского компонента об изменениях
            console.log('Измененные объекты:', changedObjects);
        }
    }, [changedObjects]);

    // Автоматическое раскрытие комплектаций при поиске в комплектации
    React.useEffect(() => {
        if (searchInComplect && searchText) {
            const newExpandedComplect = {};
            // Получаем отфильтрованные данные
            const filteredData = getSortedData();
            Object.keys(filteredData).forEach(groupName => {
                filteredData[groupName].forEach(object => {
                    newExpandedComplect[`${groupName}_${object.fid}`] = true;
                });
            });
            setExpandedComplect(newExpandedComplect);
        } else if (!searchInComplect) {
            // Сворачиваем все комплектации при отключении поиска в комплектации
            setExpandedComplect({});
        }
    }, [searchInComplect, searchText, getSortedData]);

    // Функция для получения раскрытых элементов для конкретного объекта
    const getExpandedItemsForObject = useCallback((object) => {
        if (!searchInComplect || !searchText || !object.mounted?.complect_filtered) {
            return { expandedItems: [], foundItems: [] };
        }

        const expandedItems = [];
        const foundItems = [];
        const searchTerms = searchText.toLowerCase().trim().split(' ').filter(word => word.length > 0);

        object.mounted.complect_filtered.forEach(cf => {
            const result = expandFoundItems(cf.complect, searchTerms);
            expandedItems.push(...result.expandedItems);
            foundItems.push(...result.foundItems);
        });

        return { expandedItems, foundItems };
    }, [searchInComplect, searchText, expandFoundItems]);

    // Функция для получения всех комплектаций объекта
    const getAllComplectItems = useCallback((object) => {
        if (!object.mounted?.complect_filtered) {
            return [];
        }

        // Рекурсивная функция для инициализации всех элементов комплектации
        const initializeComplectItems = (complect, id = '') => {
            const items = [];
            if (complect) {
                complect.forEach(c => {
                    const itemId = id ? `${id}_${c.id}` : `${c.id}`;
                    items.push(itemId);
                    if (c.complect) {
                        items.push(...initializeComplectItems(c.complect, itemId));
                    }
                });
            }
            return items;
        };

        const allItems = [];
        object.mounted.complect_filtered.forEach(cf => {
            allItems.push(...initializeComplectItems(cf.complect));
        });

        return allItems;
    }, []);

    // Обновление состояния найденных элементов при поиске
    React.useEffect(() => {
        if (searchInComplect && searchText) {
            const newFoundItemsIds = {};
            const filteredData = getSortedData();

            Object.keys(filteredData).forEach(groupName => {
                filteredData[groupName].forEach(object => {
                    const result = getExpandedItemsForObject(object);
                    if (result.foundItems.length > 0) {
                        newFoundItemsIds[`${groupName}_${object.fid}`] = result.foundItems;
                    }
                });
            });

            setFoundItemsIds(newFoundItemsIds);
        } else {
            setFoundItemsIds({});
        }
    }, [searchInComplect, searchText, getSortedData, getExpandedItemsForObject]);

    // Функция для массового выбора/снятия галочек с найденных элементов
    const toggleFoundItemsSelection = useCallback((groupName, objectId, isSelected) => {
        const foundItems = foundItemsIds[`${groupName}_${objectId}`] || [];
        if (foundItems.length === 0) return;

        const currentSelected = selectedItems[groupName]?.[objectId] || [];
        const currentDisabled = disabledItems[groupName]?.[objectId] || [];

        if (isSelected) {
            // При выборе: фильтруем элементы в зависимости от режима
            const toAdd = foundItems.map(fountItem => {
                const matchingItems = currentDisabled.filter(i =>
                    i === fountItem || fountItem.startsWith(i + '_') || i.startsWith(fountItem + '_')
                );

                // Фильтруем элементы в зависимости от режима
                return matchingItems.filter(itemId => {
                    if (mode) {
                        // В режиме Mode = true не включаем элементы из defaultSelectedItems, но только если тип опоры не был изменен
                        return typeChangedObjects.hasOwnProperty(objectId) || !defaultSelectedItems[groupName]?.[objectId]?.includes(itemId);
                    } else {
                        // В режиме Mode = false не включаем элементы из defaultDisabledItems
                        return !defaultDisabledItems[groupName]?.[objectId]?.includes(itemId);
                    }
                });
            }).flat();

            // Добавляем найденные элементы к выбранным
            const newSelected = [...new Set([...currentSelected, ...toAdd])];

            // Убираем найденные элементы из отключенных
            const newDisabled = currentDisabled.filter(id => !newSelected.includes(id));

            setSelectedItems(prev => ({
                ...prev,
                [groupName]: {
                    ...prev[groupName],
                    [objectId]: newSelected
                }
            }));

            setDisabledItems(prev => ({
                ...prev,
                [groupName]: {
                    ...prev[groupName],
                    [objectId]: newDisabled
                }
            }));
        } else {
            // При снятии: фильтруем элементы в зависимости от режима
            const toRemove = foundItems.map(fountItem => {
                const matchingItems = currentSelected.filter(i =>
                    i === fountItem || i.startsWith(fountItem + '_')
                ) || [];

                // Фильтруем элементы в зависимости от режима
                return matchingItems.filter(itemId => {
                    if (mode) {
                        // В режиме Mode = true не снимаем элементы из defaultSelectedItems, но только если тип опоры не был изменен
                        return typeChangedObjects.hasOwnProperty(objectId) || !defaultSelectedItems[groupName]?.[objectId]?.includes(itemId);
                    } else {
                        // В режиме Mode = false не снимаем элементы из defaultDisabledItems
                        return !defaultDisabledItems[groupName]?.[objectId]?.includes(itemId);
                    }
                });
            }).flat();

            // Проверяем всех прародителей и снимаем галочки с тех, у которых больше нет выбранных дочерних элементов
            const checkAndRemoveParents = (currentItemId) => {
                const parentId = currentItemId.split('_').slice(0, -1).join('_');
                if (!parentId) return; // Если это корневой элемент, выходим

                // Получаем все дочерние элементы родителя
                const parentChildren = currentSelected.filter(i =>
                    i.startsWith(parentId + '_') && !i.substring(parentId.length + 1).includes('_')
                ) || [];

                // Проверяем, есть ли у родителя выбранные дочерние элементы (не в toRemove)
                const remainingSelectedChildren = parentChildren.filter(childId =>
                    !toRemove.includes(childId) &&
                    !currentDisabled.includes(childId)
                );

                // Если у родителя больше нет выбранных дочерних элементов, добавляем его в toRemove
                if (remainingSelectedChildren.length === 0 && !toRemove.includes(parentId)) {
                    toRemove.push(parentId);
                    // Рекурсивно проверяем прародителя
                    checkAndRemoveParents(parentId);
                }
            };

            // Проверяем всех прародителей
            foundItems.map(foundItem => checkAndRemoveParents(foundItem));

            setSelectedItems(prev => ({
                ...prev,
                [groupName]: {
                    ...prev[groupName],
                    [objectId]: (prev[groupName]?.[objectId] || []).filter(i => !toRemove.includes(i))
                }
            }));

            setDisabledItems(prev => ({
                ...prev,
                [groupName]: {
                    ...prev[groupName],
                    [objectId]: [
                        ...(prev[groupName]?.[objectId] || []).filter(i => !toRemove.includes(i)),
                        ...toRemove
                    ]
                }
            }));
        }
    }, [foundItemsIds, selectedItems, disabledItems, mode, defaultSelectedItems, defaultDisabledItems, typeChangedObjects]);

    // Функция для массового выбора/снятия галочек со всех комплектаций в группе
    const toggleGroupComplectSelection = useCallback((groupName, isChecked) => {
        // Получаем отфильтрованные данные для этой группы
        const sortedData = getSortedData();
        const visibleObjects = sortedData[groupName] || [];

        visibleObjects.forEach(object => {
            const objectId = object.fid;
            const currentSelected = selectedItems[groupName]?.[objectId] || [];
            const currentDisabled = disabledItems[groupName]?.[objectId] || [];

            // Получаем все доступные комплектации для этого объекта
            const allComplectIds = getAllComplectItems(object);

            // if ((!mode && currentSelected.length === 0) || (mode && isChecked)) {
            if ((!mode && currentSelected.length === 0) || (mode && currentSelected.filter(selectedItem => !defaultSelectedItems[groupName]?.[objectId]?.includes(selectedItem)).length === 0)) {
                // При выборе: фильтруем элементы в зависимости от режима
                const toEnable = currentDisabled.filter(itemId => {
                    if (mode) {
                        // В режиме Mode = true не включаем элементы из defaultSelectedItems, но только если тип опоры не был изменен
                        return typeChangedObjects.hasOwnProperty(objectId) || !defaultSelectedItems[groupName]?.[objectId]?.includes(itemId);
                    } else {
                        // В режиме Mode = false не включаем элементы из defaultDisabledItems
                        return !defaultDisabledItems[groupName]?.[objectId]?.includes(itemId);
                    }
                });

                setSelectedItems(prev => ({
                    ...prev,
                    [groupName]: {
                        ...prev[groupName],
                        [objectId]: [...new Set([...currentSelected, ...toEnable])]
                    }
                }));

                setDisabledItems(prev => ({
                    ...prev,
                    [groupName]: {
                        ...prev[groupName],
                        [objectId]: currentDisabled.filter(id => !toEnable.includes(id))
                    }
                }));
            } else {
                // При снятии: фильтруем элементы в зависимости от режима
                const toDisable = currentSelected.filter(itemId => {
                    if (mode) {
                        // В режиме Mode = true не снимаем элементы из defaultSelectedItems, но только если тип опоры не был изменен
                        return typeChangedObjects.hasOwnProperty(objectId) || !defaultSelectedItems[groupName]?.[objectId]?.includes(itemId);
                    } else {
                        // В режиме Mode = false не снимаем элементы из defaultDisabledItems
                        return !defaultDisabledItems[groupName]?.[objectId]?.includes(itemId);
                    }
                });

                setSelectedItems(prev => ({
                    ...prev,
                    [groupName]: {
                        ...prev[groupName],
                        [objectId]: currentSelected.filter(id => !toDisable.includes(id))
                    }
                }));

                setDisabledItems(prev => ({
                    ...prev,
                    [groupName]: {
                        ...prev[groupName],
                        [objectId]: [...new Set([...currentDisabled, ...toDisable])]
                    }
                }));
            }
        });
    }, [getSortedData, selectedItems, disabledItems, getAllComplectItems, mode, defaultSelectedItems, defaultDisabledItems, typeChangedObjects]);

    // Внутренняя функция изменения типа объекта
    const internalHandleChangeType = useCallback((type_id, group, pillar) => {
        const newSpec = specs[group]?.find(t => t.id === type_id);
        if (!newSpec) return;

        // Отмечаем, что тип опоры был изменен, или удаляем запись если тип вернулся к исходному
        setTypeChangedObjects(prev => {
            const prevType = prev[pillar.fid];
            if (newSpec.id === prevType) {
                // Если новый тип совпадает с предыдущим, удаляем запись
                const newState = { ...prev };
                delete newState[pillar.fid];
                return newState;
            } else {
                // Иначе сохраняем новый тип
                return {
                    ...prev,
                    [pillar.fid]: pillar.mounted.id
                };
            }
        });

        // Обновляем changedObjects с новым типом, сохраняя остальные свойства mounted
        setChangedObjects(prev => ({
            ...prev,
            [pillar.fid]: {
                ...prev[pillar.fid],
                mounted: { ...pillar.mounted, ...prev[pillar.fid]?.mounted, ...newSpec, id: newSpec.id }
            }
        }));

        // Сбрасываем выбранные элементы при смене типа
        setSelectedItems(prev => ({
            ...prev,
            [group]: {
                ...prev[group],
                [pillar.fid]: []
            }
        }));

        setDisabledItems(prev => ({
            ...prev,
            [group]: {
                ...prev[group],
                [pillar.fid]: []
            }
        }));
    }, [specs]);

    // Функция применения изменений к данным
    const applyChangesToData = useCallback((data) => {
        if (!data || Object.keys(changedObjects).length === 0) {
            return data;
        }

        const result = {};
        Object.keys(data).forEach(groupName => {
            result[groupName] = data[groupName].map(obj => {
                const changes = changedObjects[obj.fid];
                if (changes) {
                    return { ...obj, ...changes };
                }
                return obj;
            });
        });

        return result;
    }, [changedObjects]);

    // Экспортируем метод для получения обновленных данных
    React.useImperativeHandle(ref, () => ({
        getUpdatedData: () => {
            return applyChangesToData(objects);
        },
        getChangedObjects: () => {
            return changedObjects;
        },
        handleChangeType: internalHandleChangeType
    }), [objects, changedObjects, applyChangesToData, internalHandleChangeType]);

    // Функция для переключения раскрытия комплектации
    const toggleComplectExpansion = useCallback((objectId, groupName) => {
        const key = `${groupName}_${objectId}`;
        setExpandedComplect(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    }, []);

    // Функция для массового выбора/снятия отметок группы
    const toggleGroupSelection = useCallback((groupName, isChecked) => {
        // Получаем отфильтрованные данные для этой группы
        const sortedData = getSortedData();
        const visibleObjects = sortedData[groupName] || [];

        if (isChecked) {
            // Включаем только видимые объекты группы - убираем их из disabledObjects
            const visibleObjectIds = visibleObjects.map(obj => obj.fid);
            setDisabledObjects(prev => ({
                ...prev,
                [groupName]: (prev[groupName] || []).filter(id => !visibleObjectIds.includes(id))
            }));
        } else {
            // Отключаем только видимые объекты группы - добавляем их в disabledObjects
            const visibleObjectIds = visibleObjects.map(obj => obj.fid);
            setDisabledObjects(prev => ({
                ...prev,
                [groupName]: [...new Set([...(prev[groupName] || []), ...visibleObjectIds])]
            }));
        }
    }, [getSortedData]);

    // Функция подготовки данных для группировки
    const prepareGroupedData = useCallback((objectsData) => {
        // Получаем отсортированные и отфильтрованные данные
        const sortedData = getSortedData();
        // Применяем изменения к данным
        const dataWithChanges = applyChangesToData(sortedData);

        const groups = ['transformers', 'pillars', 'zvz', 'opn', 'rlnd', 'runn'];
        const groupedData = [];
        const groupCounts = [];
        const groupObjectCounts = [];
        const groupNameToIndex = {}; // Маппинг имени группы на реальный индекс в groupCounts

        let realGroupIndex = 0; // Реальный индекс группы (только для непустых групп)

        groups.forEach((groupName) => {
            const groupItems = dataWithChanges[groupName] || [];
            if (groupItems.length > 0) {
                let currentGroupCount = 0; // количество строк (включая раскрытые комплектации)
                let currentGroupObjectsCount = 0; // количество объектов (без комплектаций)

                groupItems.forEach(item => {
                    // Добавляем основную строку объекта
                    groupedData.push({
                        ...item,
                        group: realGroupIndex,
                        groupName: groupName,
                        groupDisplayName: groupName === 'transformers' ? 'Трансформаторы' :
                            groupName === 'pillars' ? 'Опоры' :
                                groupName === 'zvz' ? 'Комплекты адаптеров ВЗ' :
                                    groupName === 'opn' ? 'Комплекты ОПН' :
                                        groupName === 'rlnd' ? 'РЛНД' : 'РУНН',
                        isComplectRow: false
                    });
                    currentGroupCount++;
                    currentGroupObjectsCount++;

                    // Если комплектация раскрыта, добавляем строку комплектации
                    if (expandedComplect[`${groupName}_${item.fid}`]) {
                        groupedData.push({
                            ...item,
                            group: realGroupIndex,
                            groupName: groupName,
                            groupDisplayName: groupName === 'transformers' ? 'Трансформаторы' :
                                groupName === 'pillars' ? 'Опоры' :
                                    groupName === 'zvz' ? 'Комплекты адаптеров ВЗ' :
                                        groupName === 'opn' ? 'Комплекты ОПН' :
                                            groupName === 'rlnd' ? 'РЛНД' : 'РУНН',
                            isComplectRow: true
                        });
                        currentGroupCount++;
                    }
                });

                groupNameToIndex[groupName] = realGroupIndex;
                groupCounts.push(currentGroupCount);
                groupObjectCounts.push(currentGroupObjectsCount);
                realGroupIndex++;
            }
        });

        // Добавляем пустые элементы в конец для принудительного рендеринга
        const paddingItems = Array.from({ length: Math.max(1, groupCounts.length) }, (_, index) => ({
            id: `padding-${index}`,
            fid: groupedData.length + index + 1,
            group: groupedData.length > 0 ? groupedData[groupedData.length - 1].group : 0,
            groupName: groupedData.length > 0 ? groupedData[groupedData.length - 1].groupName : 'pillars',
            isPadding: true
        }));

        return {
            groupedData: [...groupedData, ...paddingItems],
            groupCounts,
            groupObjectCounts,
            groupNameToIndex // Добавляем маппинг для использования в других местах
        };
    }, [expandedComplect, applyChangesToData, getSortedData]);

    // Получаем подготовленные данные для виртуализации
    const groupedData = useMemo(() => {
        return prepareGroupedData(objects);
    }, [objects, prepareGroupedData]);

    // Мемоизируем компоненты виртуозо, чтобы избежать пересоздания и сброса скролла
    const virtuosoComponents = useMemo(() => createVirtuosoTableComponents(darkMode), [darkMode]);

    // Функция навигации по группам
    const scrollToGroup = useCallback((groupIndex) => {
        if (virtuosoRef.current && groupIndex >= 0 && groupIndex < groupedData.groupCounts.length) {
            const startIndex = groupedData.groupCounts.slice(0, groupIndex).reduce((sum, count) => sum + count, 0);
            virtuosoRef.current.scrollToIndex({ index: startIndex, align: 'start' });
        }
    }, [groupedData.groupCounts]);

    // Функция для заголовка таблицы
    const fixedHeaderContent = useCallback(() => {
        // Получаем только видимые столбцы для компактного режима
        const visibleColumnsArray = smallMode
            ? columns.filter(col => ['checkbox', 'location', 'pillar', 'complect', 'retreat'].includes(col.id))
            : columns;



        return (
            <TableRow>
                {visibleColumnsArray.map((column, index) => (
                    <TableCell
                        key={column.id}
                        variant="head"
                        align={column.align || 'left'}
                        style={{
                            width: column.width || column.minWidth,
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth || column.minWidth
                        }}
                        colSpan={column.colSpan}
                        sx={{
                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                            color: darkMode ? '#f5f7fa' : '#333',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            borderBottom: darkMode
                                ? '1px solid rgba(255, 255, 255, 0.12)'
                                : '2px solid rgba(0, 0, 0, 0.12)',
                            borderRight: darkMode
                                ? '1px solid rgba(255, 255, 255, 0.08)'
                                : '1px solid rgba(0, 0, 0, 0.08)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            p: '4px 8px',

                        }}
                        onClick={() => {
                            // Только для сортируемых столбцов
                            if (['pillar', 'transformer', 'type', 'category'].includes(column.id)) {
                                handleSort(column.id);
                            }
                        }}
                    >
                        {['pillar', 'transformer', 'type', 'category'].includes(column.id) ? (
                            <TableSortLabel
                                active={sortConfig.key === column.id}
                                direction={sortConfig.key === column.id ? sortConfig.direction : 'asc'}
                                onClick={() => handleSort(column.id)}
                                sx={{
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        borderRadius: '4px',
                                    },
                                    '&.Mui-active': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        fontWeight: 700,
                                    },
                                    '& .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        fontSize: '1rem',
                                        opacity: 0,
                                        transition: 'all 0.2s ease-in-out',
                                    },
                                    '&.Mui-active .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        opacity: 1,
                                    },
                                    '&:hover .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        opacity: 1,
                                    },
                                }}
                            >
                                {column.label}
                            </TableSortLabel>
                        ) : (
                            column.label
                        )}
                    </TableCell>
                ))}
            </TableRow>
        );
    }, [smallMode, sortConfig, handleSort, darkMode]);

    // Функция для заголовка группы
    const groupContent = useCallback((index) => {
        if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
            console.log('No grouped data in groupContent');
            return null;
        }
        const firstItemInGroup = groupedData.groupedData.find(item => item.group === index);
        if (!firstItemInGroup) return null
        const groupName = firstItemInGroup?.groupName || `group_${index}`;
        const groupDisplayName = firstItemInGroup?.groupDisplayName || `Группа ${index}`;
        const groupCount = groupedData.groupCounts[index];
        const groupObjectsCount = groupedData.groupObjectCounts?.[index] ?? 0;

        // Определяем состояние checkbox для группы на основе отображаемых строк
        const sortedData = getSortedData();
        const visibleObjects = sortedData[groupName] || [];
        const disabledObjectsInGroup = disabledObjects[groupName] || [];

        // Проверяем состояние только для видимых объектов
        const visibleObjectIds = visibleObjects.map(obj => obj.fid);
        const disabledVisibleObjects = disabledObjectsInGroup.filter(id => visibleObjectIds.includes(id));

        const isAllSelected = visibleObjects.length > 0 && disabledVisibleObjects.length === 0;
        const isPartiallySelected = disabledVisibleObjects.length > 0 && disabledVisibleObjects.length < visibleObjects.length;
        // Определяем состояние второго чекбокса для комплектаций
        let isAllComplectSelected = !Object.values(disabledItems[groupName]).some(v => v.length > 0);
        let isPartiallyComplectSelected = !isAllComplectSelected && Object.values(selectedItems[groupName]).some(v => v.length > 0);



        // Получаем количество видимых столбцов
        const visibleColumnsArray = smallMode
            ? columns.filter(col => ['checkbox', 'location', 'pillar', 'complect', 'retreat'].includes(col.id))
            : columns;
        const colSpan = visibleColumnsArray.reduce((a, v) => a + (v.colSpan || 1), 0) - 3;

        const cellStyle = {
            backgroundColor: darkMode ? '#2a2a2a' : '#f0f0f0',
            color: darkMode ? '#64b5f6' : '#1976d2',
            fontWeight: 600,
            fontSize: '0.9rem',
            padding: '6px 16px 3px 16px',
            borderBottom: darkMode ? '2px solid rgba(255, 255, 255, 0.12)' : '2px solid rgba(0, 0, 0, 0.12)',
            borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
            position: 'relative',
            zIndex: 0,
        }

        return (
            [<td align='center' style={cellStyle}>
                <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isPartiallySelected}
                    onChange={(e) => toggleGroupSelection(groupName, e.target.checked)}
                    sx={{
                        padding: '2px',
                        color: darkMode ? '#64b5f6' : '#1976d2',
                        '&.Mui-checked': {
                            color: darkMode ? '#64b5f6' : '#1976d2',
                        },
                        '&.MuiCheckbox-indeterminate': {
                            color: darkMode ? '#64b5f6' : '#1976d2',
                        },
                        '&:hover': {
                            backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                        }
                    }}
                />
            </td>,
            <td align='center' style={cellStyle}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                    {groupDisplayName}
                </Typography>
            </td>,
            <td align='center' style={cellStyle}>
                <Checkbox
                    size="small"
                    checked={isAllComplectSelected}
                    indeterminate={isPartiallyComplectSelected}
                    onChange={(e) => toggleGroupComplectSelection(groupName, e.target.checked)}
                    sx={{
                        padding: '2px',
                        color: isAllComplectSelected
                            ? (darkMode ? '#4caf50' : '#2e7d32')  // Зеленый если все выбраны
                            : (darkMode ? '#ff9800' : '#f57c00'),  // Оранжевый если не все выбраны
                        '&.Mui-checked': {
                            color: darkMode ? '#4caf50' : '#2e7d32',
                        },
                        '&.MuiCheckbox-indeterminate': {
                            color: darkMode ? '#ff9800' : '#f57c00',
                        },
                        '&:hover': {
                            backgroundColor: isAllComplectSelected
                                ? (darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)')
                                : (darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(245, 124, 0, 0.1)'),
                        }
                    }}
                />
            </td>,
            <td align='right' colSpan={colSpan} style={cellStyle}>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'end',
                    justifyContent: 'end',
                    gap: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Кнопки для массового выбора найденных элементов */}
                        {searchInComplect && searchText && (
                            <>
                                <Tooltip title="Выбрать все найденные элементы в комплектации" arrow placement="top">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<CheckCircleIcon fontSize="small" />}
                                        onClick={() => {
                                            visibleObjects.forEach(object => {
                                                toggleFoundItemsSelection(groupName, object.fid, true);
                                            });
                                        }}
                                        sx={{
                                            fontSize: '0.7rem',
                                            padding: '4px 8px',
                                            minWidth: 'auto',
                                            color: darkMode ? '#4caf50' : '#2e7d32',
                                            borderColor: darkMode ? '#4caf50' : '#2e7d32',
                                            borderRadius: '6px',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
                                                borderColor: darkMode ? '#66bb6a' : '#388e3c',
                                                transform: 'scale(1.02)',
                                            }
                                        }}
                                    >
                                        {smallMode ? 'Выбрать' : 'Выбрать найденные'}
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Снять выбор со всех найденных элементов в комплектации" arrow placement="top">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<CancelIcon fontSize="small" />}
                                        onClick={() => {
                                            visibleObjects.forEach(object => {
                                                toggleFoundItemsSelection(groupName, object.fid, false);
                                            });
                                        }}
                                        sx={{
                                            fontSize: '0.7rem',
                                            padding: '4px 8px',
                                            minWidth: 'auto',
                                            color: darkMode ? '#f44336' : '#d32f2f',
                                            borderColor: darkMode ? '#f44336' : '#d32f2f',
                                            borderRadius: '6px',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                                borderColor: darkMode ? '#ef5350' : '#f44336',
                                                transform: 'scale(1.02)',
                                            }
                                        }}
                                    >
                                        {smallMode ? 'Снять' : 'Снять найденные'}
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                        <Chip
                            label={`${groupObjectsCount}`}
                            size="small"
                            onClick={() => { }}
                            sx={{
                                bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                border: darkMode ? '1px solid rgba(100, 181, 246, 0.3)' : '1px solid rgba(25, 118, 210, 0.3)',
                            }}
                        />
                    </Box>
                </Box>
            </td>]
        );
    }, [groupedData, smallMode, darkMode, getSortedData, disabledObjects, toggleGroupSelection, toggleGroupComplectSelection, searchInComplect, searchText, toggleFoundItemsSelection, selectedItems, disabledItems, getAllComplectItems]);



    // Компонент навигации по группам
    const GroupNavigation = useCallback(() => {
        const groups = [
            { name: 'transformers', displayName: 'Трансформаторы' },
            { name: 'pillars', displayName: 'Опоры' },
            { name: 'zvz', displayName: 'Комплекты адаптеров ВЗ' },
            { name: 'opn', displayName: 'Комплекты ОПН' },
            { name: 'rlnd', displayName: 'РЛНД' },
            { name: 'runn', displayName: 'РУНН' }
        ];

        // Фильтруем только группы, которые есть в данных
        const visibleGroups = groups.filter(group => {
            const realIndex = groupedData.groupNameToIndex?.[group.name];
            return realIndex !== undefined && groupedData.groupCounts[realIndex] > 0;
        });

        if (visibleGroups.length <= 1) return null;

        // Проверяем, есть ли группы с найденными элементами при поиске
        const hasFoundItems = searchInComplect && searchText &&
            Object.values(foundItemsIds).some(items => items.length > 0);

        // Функция для получения количества найденных элементов в группе
        const getFoundItemsCount = (groupName) => {
            if (!hasFoundItems) return 0;
            if (!groupName) return 0;

            const groupObjects = objects[groupName] || [];
            return groupObjects.reduce((count, object) => {
                const foundItems = foundItemsIds[`${groupName}_${object.fid}`] || [];
                return count + foundItems.length;
            }, 0);
        };

        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 16px',
                backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
                borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                gap: 1,
                flexWrap: 'wrap',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{
                        color: darkMode ? '#f5f7fa' : '#333',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                    }}>
                        Группы:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {visibleGroups.map((group) => {
                            const realIndex = groupedData.groupNameToIndex?.[group.name];
                            if (realIndex === undefined) return null;

                            const foundCount = getFoundItemsCount(group.name);
                            const hasFoundItemsInGroup = foundCount > 0;

                            return (
                                <Button
                                    key={group.name}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => scrollToGroup(realIndex)}
                                    sx={{
                                        fontSize: '0.75rem',
                                        padding: '4px 8px',
                                        minWidth: 'auto',
                                        textTransform: 'none',
                                        backgroundColor: 'transparent',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        borderColor: hasFoundItemsInGroup
                                            ? (darkMode ? '#ffb74d' : '#f57c00')
                                            : (darkMode ? '#64b5f6' : '#1976d2'),
                                        borderRadius: '6px',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: hasFoundItemsInGroup
                                                ? (darkMode ? 'rgba(255, 183, 77, 0.1)' : 'rgba(245, 124, 0, 0.1)')
                                                : (darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)'),
                                            transform: 'scale(1.02)',
                                        }
                                    }}
                                >
                                    {group.displayName}
                                    <Chip
                                        label={groupedData.groupObjectCounts?.[realIndex] ?? groupedData.groupCounts[realIndex]}
                                        size="small"
                                        sx={{
                                            ml: 0.5,
                                            height: '16px',
                                            fontSize: '0.65rem',
                                            backgroundColor: hasFoundItemsInGroup
                                                ? (darkMode ? 'rgba(255, 183, 77, 0.2)' : 'rgba(245, 124, 0, 0.2)')
                                                : (darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)'),
                                            color: hasFoundItemsInGroup
                                                ? (darkMode ? '#ffb74d' : '#f57c00')
                                                : (darkMode ? '#64b5f6' : '#1976d2'),
                                            border: 'none',
                                        }}
                                    />
                                    {hasFoundItemsInGroup && (
                                        <Chip
                                            label={`${foundCount} найдено`}
                                            size="small"
                                            sx={{
                                                ml: 0.5,
                                                height: '16px',
                                                fontSize: '0.6rem',
                                                backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.2)',
                                                color: darkMode ? '#4caf50' : '#2e7d32',
                                                border: 'none',
                                                fontWeight: 'bold',
                                            }}
                                        />
                                    )}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        );
    }, [groupedData.groupCounts, darkMode, scrollToGroup, searchInComplect, searchText, foundItemsIds, objects]);

    // Если нет данных, показываем сообщение
    if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
                width: '100%',
                backgroundColor: darkMode ? '#1e1e1e' : '#f5f7fa',
                borderRadius: '8px',
                border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: darkMode ? '0 4px 8px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    textAlign: 'center',
                    padding: 3,
                }}>
                    <Box sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 1,
                    }}>
                        <Typography
                            variant="h3"
                            sx={{
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                fontSize: '2rem',
                                fontWeight: 300,
                            }}
                        >
                            📋
                        </Typography>
                    </Box>
                    <Typography
                        variant="h5"
                        sx={{
                            color: darkMode ? '#e0e0e0' : '#424242',
                            fontWeight: 500,
                            marginBottom: 1,
                        }}
                    >
                        Нет данных для отображения
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: darkMode ? '#bbb' : '#666',
                            fontSize: '0.9rem',
                            maxWidth: '400px',
                            lineHeight: 1.5,
                        }}
                    >
                        Попробуйте изменить параметры поиска или фильтры для отображения данных
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', width: '100%', }}>
            <GroupNavigation />
            <GroupedTableVirtuoso
                ref={virtuosoRef}
                key={'grouped-table'}
                data={groupedData.groupedData}
                overscan={900}
                style={{
                    backgroundColor: darkMode ? '#1e1e1e' : '#f5f7fa',
                }}
                groupCounts={groupedData.groupCounts}
                totalCount={groupedData.groupedData.length}
                components={virtuosoComponents}
                fixedHeaderContent={fixedHeaderContent}
                groupContent={groupContent}
                computeItemKey={(index) => {
                    const item = groupedData.groupedData[index];
                    if (!item) return `unknown-${index}`;
                    if (item.isPadding) return `pad-${index}`;
                    return `${item.groupName}-${item.fid}${item.isComplectRow ? '-c' : ''}`;
                }}
                itemContent={(index) => {
                    const object = groupedData.groupedData[index];
                    if (!object) {
                        console.log('No object found for index:', index);
                        return null;
                    }
                    console.log(object)
                    // Если это padding элемент, возвращаем пустую строку
                    if (object.isPadding) {
                        const visibleColumnsArray = smallMode
                            ? columns.filter(col => ['checkbox', 'location', 'pillar', 'complect', 'retreat'].includes(col.id))
                            : columns;

                        return (
                            <React.Fragment>
                                {visibleColumnsArray.map((column) => (
                                    <TableCell key={column.id} sx={{
                                        height: '20px',
                                        minHeight: '20px',
                                        padding: '4px 8px',
                                        border: 'none',
                                        backgroundColor: 'transparent'
                                    }} />
                                ))}
                            </React.Fragment>
                        );
                    }

                    // Если это строка комплектации, рендерим компонент комплектации
                    if (object.isComplectRow) {
                        const groupName = object.groupName;
                        const isDisabled = !!disabledObjects[groupName]?.find(id => id === object.fid);
                        const allChecked = disabledItems[groupName]?.[object.fid]?.length === 0;

                        // Фон строки комплектации остается нейтральным

                        // Получаем количество видимых столбцов для комплектации
                        const visibleColumnsArray = smallMode
                            ? columns.filter(col => ['checkbox', 'location', 'pillar', 'complect', 'retreat'].includes(col.id))
                            : columns;
                        const colSpan = visibleColumnsArray.reduce((a, v) => a + (v.colSpan || 1), 0);

                        return (
                            <TableCell
                                colSpan={colSpan}
                                sx={{
                                    padding: 0,
                                    border: 'none',
                                    backgroundColor: 'transparent'
                                }}
                            >
                                <ComplectExpanded
                                    object={object}
                                    groupName={object.groupName}
                                    mode={mode}
                                    specs={specs}
                                    selectedItems={selectedItems}
                                    setSelectedItems={setSelectedItems}
                                    disabledItems={disabledItems}
                                    setDisabledItems={setDisabledItems}
                                    defaultSelectedItems={defaultSelectedItems}
                                    defaultDisabledItems={defaultDisabledItems}
                                    handleChangeType={internalHandleChangeType}
                                    smallMode={smallMode}
                                    isExpanded={true}
                                    onToggle={() => toggleComplectExpansion(object.fid, object.groupName)}
                                    searchText={searchText}
                                    darkMode={darkMode}
                                    matchesSearch={matchesSearch}
                                    matchesSearchRecursive={matchesSearchRecursive}
                                    highlightText={highlightText}
                                    expandFoundItems={expandFoundItems}
                                    getExpandedItemsForObject={getExpandedItemsForObject}
                                    typeChangedObjects={typeChangedObjects}
                                    itemMetadata={itemMetadata}
                                />
                            </TableCell>
                        );
                    }

                    // Обычная строка объекта
                    const groupName = object.groupName;
                    const isDisabled = !!disabledObjects[groupName]?.find(id => id === object.fid);
                    const allChecked = disabledItems[groupName]?.[object.fid]?.length === 0;

                    // Фон строки остается нейтральным, индикатор - кнопка раскрытия комплектации

                    // Общий стиль для всех TableCell
                    const cellStyle = {
                        minHeight: '40px',
                        height: 'auto',
                        padding: '2px 4px',
                        fontSize: '0.75rem',
                        color: darkMode ? '#f5f7fa' : '#333',
                        borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                        borderRight: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                    };

                    const isComplectExpanded = expandedComplect[`${groupName}_${object.fid}`];

                    return (
                        <React.Fragment>
                            {/* Чекбокс включения/выключения объекта */}
                            <TableCell sx={cellStyle} align="center">
                                <Checkbox
                                    size="small"
                                    sx={{
                                        padding: '2px',
                                        // color: darkMode ? '#64b5f6' : '#2196F3',
                                        // '&:hover': {
                                        //     backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                                        //     color: darkMode ? '#90caf9' : '#1976D2'
                                        // }

                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        '&.Mui-checked': {
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                        '&.MuiCheckbox-indeterminate': {
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        }
                                    }}
                                    checked={!isDisabled}
                                    onChange={(e) => {
                                        if (isDisabled) {
                                            setDisabledObjects(prev => ({
                                                ...prev,
                                                [groupName]: prev[groupName].filter(id => id !== object.fid)
                                            }))
                                        } else {
                                            setDisabledObjects(prev => ({
                                                ...prev,
                                                [groupName]: [...(prev[groupName] || []), object.fid]
                                            }))
                                        }
                                    }}
                                />
                            </TableCell>
                            {/* Кнопка перехода к объекту на карте */}
                            {/* <TableCell sx={cellStyle} align="center">
                                <LocationOnIcon
                                    fontSize="small"
                                    style={{
                                        color: isDisabled ? (darkMode ? '#666' : '#999') : 'inherit',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => {
                                        if (!isDisabled) {
                                            aimToObject(map, object)
                                        }
                                    }}
                                />
                            </TableCell> */}
                            {/* Имя объекта */}
                            <TableCell sx={cellStyle} align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>

                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            selectObject(object)
                                        }}
                                        sx={{
                                            padding: '2px',
                                            color: darkMode ? '#64b5f6' : '#2196F3',
                                            '&:hover': {
                                                backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                                                color: darkMode ? '#90caf9' : '#1976D2'
                                            }
                                        }}
                                        title="Открыть обзор опоры в новой вкладке"
                                    >
                                        {groupName === 'pillars' && (
                                            <Box sx={{ position: 'relative', width: 28, height: 28 }}>
                                                <Box style={{ position: 'absolute', margin: '4px 0 0 8px' }}>
                                                    {pillarIcon(object.type, object.status, object.material_type, object.supports_count, 12)}

                                                </Box>
                                                {[2, 3, 4, 7].includes(Number(object?.layer)) ? (
                                                    <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                                        {projectStatusOreolIcon('pillar', Number(object.layer), 28)}
                                                    </Box>
                                                ) : null}
                                            </Box>
                                        )}

                                        {groupName === 'zvz' && (
                                            zvzIcon(24)
                                        )}

                                        {groupName === 'opn' && (
                                            opnIcon(24)
                                        )}


                                        {groupName === 'transformers' && (
                                            <Box sx={{ position: 'relative', width: 36, height: 36 }}>
                                                <Box style={{ position: 'absolute', margin: '8px 0 0 9px' }}>

                                                    {ktpIcon(object.accounting ? 'accounting' : 'default', 18, 18)}
                                                </Box>

                                                {['2', '3', '4', '7'].includes(object.layer) ?
                                                    <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                                        {projectStatusOreolIcon('ktp', Number(object.layer), 36)}
                                                    </Box>
                                                    : null}
                                            </Box>
                                            
                                        )}

                                        {groupName === 'runn' && (
                                            runnIcon(24)
                                        )}

                                        {groupName === 'rlnd' && (
                                            rlndIcon(20)
                                        )}


                                    </IconButton>

                                    <Typography variant="caption">
                                        {object.short_name || object.name}
                                    </Typography>

                                    {smallMode && groupName === 'pillars' && object.mounted?.name &&
                                        <Chip
                                            label={object.mounted.name.split(' ')[0]}
                                            size="small"
                                            sx={{
                                                fontSize: '0.65rem',
                                                // height: '16px',
                                                bgcolor: object.spec?.id === object.mounted?.id
                                                    ? (darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)')
                                                    : (darkMode ? 'rgba(255, 183, 77, 0.2)' : 'rgba(255, 183, 77, 0.2)'),
                                                color: object.spec?.id === object.mounted?.id
                                                    ? (darkMode ? '#64b5f6' : '#1976d2')
                                                    : (darkMode ? '#ffb74d' : '#f57c00'),
                                                border: object.spec?.id === object.mounted?.id
                                                    ? (darkMode ? '1px solid rgba(100, 181, 246, 0.3)' : '1px solid rgba(25, 118, 210, 0.3)')
                                                    : (darkMode ? '1px solid rgba(255, 183, 77, 0.4)' : '1px solid rgba(255, 183, 77, 0.4)'),
                                            }}
                                        />
                                    }
                                </Box>
                            </TableCell>
                            {/* Кнопка раскрытия комплектации */}
                            <TableCell sx={cellStyle} align="center">
                                <Tooltip
                                    title={isComplectExpanded ? 'Скрыть комплектацию' : 'Показать комплектацию'}
                                    arrow
                                    placement="top"
                                >
                                    <IconButton
                                        size="small"
                                        disabled={isDisabled}
                                        onClick={() => {
                                            if (!isDisabled) {
                                                toggleComplectExpansion(object.fid, groupName);
                                            }
                                        }}
                                        sx={{
                                            // Определяем цвет в зависимости от состояния
                                            color: isDisabled
                                                ? (darkMode ? '#666' : '#999')
                                                : allChecked
                                                    ? (darkMode ? '#4caf50' : '#2e7d32')
                                                    : (darkMode ? '#ff9800' : '#f57c00'),
                                            backgroundColor: isDisabled
                                                ? (darkMode ? 'rgba(102, 102, 102, 0.1)' : 'rgba(153, 153, 153, 0.1)')
                                                : allChecked
                                                    ? (darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)')
                                                    : (darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(245, 124, 0, 0.1)'),
                                            border: isDisabled
                                                ? (darkMode ? '1px solid rgba(102, 102, 102, 0.3)' : '1px solid rgba(153, 153, 153, 0.3)')
                                                : allChecked
                                                    ? (darkMode ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(46, 125, 50, 0.3)')
                                                    : (darkMode ? '1px solid rgba(255, 152, 0, 0.3)' : '1px solid rgba(245, 124, 0, 0.3)'),
                                            padding: '2px',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: isDisabled
                                                    ? (darkMode ? 'rgba(102, 102, 102, 0.1)' : 'rgba(153, 153, 153, 0.1)')
                                                    : allChecked
                                                        ? (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(46, 125, 50, 0.2)')
                                                        : (darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(245, 124, 0, 0.2)'),
                                                borderColor: isDisabled
                                                    ? (darkMode ? '#666' : '#999')
                                                    : allChecked
                                                        ? (darkMode ? '#66bb6a' : '#388e3c')
                                                        : (darkMode ? '#ffb74d' : '#ff9800'),
                                                transform: isDisabled ? 'none' : 'scale(1.05)',
                                            },
                                            '&.Mui-disabled': {
                                                color: darkMode ? '#666' : '#999',
                                                borderColor: darkMode ? '#666' : '#999',
                                                backgroundColor: darkMode ? 'rgba(102, 102, 102, 0.1)' : 'rgba(153, 153, 153, 0.1)',
                                            }
                                        }}
                                    >
                                        {isComplectExpanded ?
                                            <ExpandLessIcon fontSize="small" /> :
                                            <ExpandMoreIcon fontSize="small" />
                                        }
                                    </IconButton>
                                </Tooltip>

                            </TableCell>
                            {/* Отступление */}
                            <TableCell sx={cellStyle} align="center">
                                <TextField
                                    value={object.mounted?.retreat || ''}
                                    error={false}
                                    multiline
                                    maxRows={4}
                                    maxLength={255}
                                    size="small"
                                    // placeholder=""
                                    onChange={(e) => {
                                        setChangedObjects(prev => ({
                                            ...prev,
                                            [object.fid]: {
                                                ...prev[object.fid],
                                                mounted: { ...object.mounted, ...prev[object.fid]?.mounted, retreat: e.target.value }
                                            }
                                        }));
                                    }}
                                    sx={{
                                        width: '100%',
                                        minWidth: smallMode ? '180px' : '200px',
                                        maxWidth: smallMode ? '220px' : '250px',
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            fontSize: smallMode ? '0.7rem' : '0.75rem',
                                            lineHeight: 1.1,
                                            padding: smallMode ? '6px 10px' : '8px 12px',
                                            '&::placeholder': {
                                                color: darkMode ? 'rgba(245, 247, 250, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                opacity: 1,
                                                fontSize: smallMode ? '0.7rem' : '0.75rem',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                            fontSize: smallMode ? '0.7rem' : '0.75rem',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            p: 0,
                                            backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
                                            borderRadius: '6px',
                                            fontSize: smallMode ? '0.7rem' : '0.75rem',
                                            minHeight: smallMode ? '28px' : '32px',
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                                borderWidth: '1px',
                                                transition: 'border-color 0.2s ease-in-out',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: darkMode ? 'rgba(100, 181, 246, 0.6)' : 'rgba(25, 118, 210, 0.6)',
                                                borderWidth: '1px',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                                borderWidth: '2px',
                                            },
                                            '&.Mui-error fieldset': {
                                                borderColor: darkMode ? '#f44336' : '#d32f2f',
                                                borderWidth: '2px',
                                            },
                                        },
                                        '& .MuiFormHelperText-root': {
                                            fontSize: '0.65rem',
                                            marginTop: '2px',
                                            color: darkMode ? '#f44336' : '#d32f2f',
                                        },
                                    }}
                                />
                            </TableCell>
                            {/* Дополнительные колонки для полного режима */}
                            {!smallMode && (
                                <>
                                    <TableCell sx={cellStyle} align="center">
                                        {object.ktp && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        selectObject(object.ktp);
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        color: darkMode ? '#64b5f6' : '#2196F3',
                                                        '&:hover': {
                                                            backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                                                            color: darkMode ? '#90caf9' : '#1976D2'
                                                        }
                                                    }}
                                                    title="Открыть обзор опоры в новой вкладке"
                                                >
                                                    {/* <LocationOnIcon sx={{ fontSize: { xs: '12px', md: '16px' } }} /> */}
                                                    <Box sx={{ position: 'relative', width: 32, height: 32 }}>
                                                        <Box style={{ position: 'absolute', margin: '5px 0 0 6px' }}>
                                                            {ktpIcon('default', 20, 20)}
                                                        </Box>
                                                        {[2, 3, 4, 7].includes(Number(object.ktp?.layer)) ? (
                                                            <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                                                {projectStatusOreolIcon('ktp', Number(object.ktp?.layer), 32)}
                                                            </Box>
                                                        ) : null}
                                                    </Box>
                                                </IconButton>
                                                <Typography variant="caption" sx={{ lineHeight: 1.1 }}>
                                                    {`${object.ktp?.name}${object.ktp?.town ? ' (' + object.ktp?.town + ')' : ''}`}
                                                </Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        <Chip
                                            label={object.mounted?.name || 'Не указано'}
                                            size="small"
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: '20px',
                                                bgcolor: object.spec?.id === object.mounted?.id
                                                    ? (darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)')
                                                    : (darkMode ? 'rgba(255, 183, 77, 0.2)' : 'rgba(255, 183, 77, 0.2)'),
                                                color: object.spec?.id === object.mounted?.id
                                                    ? (darkMode ? '#64b5f6' : '#1976d2')
                                                    : (darkMode ? '#ffb74d' : '#f57c00'),
                                                border: object.spec?.id === object.mounted?.id
                                                    ? (darkMode ? '1px solid rgba(100, 181, 246, 0.3)' : '1px solid rgba(25, 118, 210, 0.3)')
                                                    : (darkMode ? '1px solid rgba(255, 183, 77, 0.4)' : '1px solid rgba(255, 183, 77, 0.4)'),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        <Chip
                                            label={object.mounted?.category || 'Не указано'}
                                            size="small"
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: '20px',
                                                bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                                color: darkMode ? '#4caf50' : '#2e7d32',
                                                border: darkMode ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(76, 175, 80, 0.3)',
                                            }}
                                        />
                                    </TableCell>
                                </>
                            )}
                        </React.Fragment>
                    );
                }}
            />
        </Box>
    );
});

export default ObjectsTable;
